import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { excelAPI } from '../services/api';
import '../styles/AppShell.css';

const COLUMNS = [
  'ID No',
  'Name',
  'Designation',
  'D.O.B',
  'DOJ',
  'D.O.Issue',
  'Mobile No',
  'Address',
];

const ExcelImport = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    const fileType = selectedFile.name.split('.').pop().toLowerCase();
    if (fileType === 'xlsx' || fileType === 'xls') {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    setUploading(true);
    setError('');
    setResults(null);
    try {
      const formData = new FormData();
      formData.append('excel', file);
      const response = await excelAPI.import(formData);
      const importResults = response.data.results;
      setResults(importResults);
      if (importResults && importResults.success === 0 && importResults.failed > 0) {
        setError(
          `No rows imported. ${importResults.failed} failed. Check errors below.`
        );
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to upload file'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await excelAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon color="primary" />
          Import from Excel
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {!results ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Use the template columns below. Dates should be DD-MM-YYYY.
            </Typography>

            <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mb: 2.5 }}>
              {COLUMNS.map((col) => (
                <Chip key={col} label={col} size="small" variant="outlined" />
              ))}
            </Stack>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ mb: 2.5 }}
            >
              Download template
            </Button>

            <div
              className={`dropzone ${file ? 'is-active' : ''}`}
              onClick={() => document.getElementById('file-input').click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  document.getElementById('file-input').click();
                }
              }}
            >
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {file ? (
                <Box>
                  <SuccessIcon sx={{ fontSize: 44, mb: 1, color: 'secondary.main' }} />
                  <Typography sx={{ fontWeight: 750, color: 'primary.main' }}>
                    File ready
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <FileIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    {file.name}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 44, mb: 1, color: 'text.secondary' }} />
                  <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Click to select Excel file
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    .xlsx or .xls
                  </Typography>
                </Box>
              )}
            </div>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 750, mb: 2 }}>
              Import complete
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip label={`Total ${results.total}`} />
              <Chip label={`Success ${results.success}`} color="success" />
              <Chip label={`Failed ${results.failed}`} color={results.failed ? 'error' : 'default'} />
            </Stack>
            {results.errors?.length > 0 && (
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                {results.errors.map((errMsg, index) => (
                  <Box key={index} display="flex" alignItems="flex-start" gap={1} mb={0.8}>
                    <ErrorIcon color="error" sx={{ fontSize: 16, mt: 0.3 }} />
                    <Typography variant="caption">{errMsg}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {!results ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!file || uploading}
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExcelImport;
